
import { defineComponent, ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { VCodeMirror } from 'vue3-code-mirror';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';

// Import des modes CodeMirror pour la coloration syntaxique
import 'codemirror/mode/css/css';
import 'codemirror/mode/htmlmixed/htmlmixed';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/xml/xml';

export default defineComponent({
  name: 'CodeMirrorModal',

  components: {
    Dialog,
    Button,
    VCodeMirror,
    VariablesButton,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    code: {
      type: String,
      default: '',
    },
    language: {
      type: String,
      default: 'css',
    },
    title: {
      type: String,
      default: 'Éditeur de code',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    showVariables: {
      type: Boolean,
      default: false,
    },
    variablesList: {
      type: Array,
      default: () => [],
    },
  },

  emits: ['update:visible', 'update:code', 'apply', 'cancel'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const codeMirrorRef = ref();
    const localCode = ref(props.code);
    const originalCode = ref(props.code);
    const codeMirrorReady = ref(false);

    const isVisible = computed({
      get: () => props.visible,
      set: (value) => emit('update:visible', value),
    });

    const cmOptions = computed(() => {
      // Configuration du mode selon le langage
      let mode;
      switch (props.language) {
        case 'css':
          mode = 'text/css';
          break;
        case 'html':
          mode = 'text/html';
          break;
        case 'javascript':
          mode = 'text/javascript';
          break;
        default:
          mode = 'text/plain';
      }

      return {
        mode,
        theme: 'default',
        lineNumbers: true,
        smartIndent: true,
        indentUnit: 4,
        foldGutter: true,
        styleActiveLine: true,
        lineWrapping: true,
        readOnly: props.readonly,
        extraKeys: {
          'Ctrl-S': () => handleApply(),
          'Esc': () => handleCancel(),
        },
      };
    });

    const getLanguageIcon = (lang: string) => {
      switch (lang) {
        case 'css':
          return 'fab fa-css3';
        case 'html':
          return 'fab fa-html5';
        case 'javascript':
          return 'fab fa-js-square';
        default:
          return 'fab fa-code';
      }
    };

    const insertVariable = (variableValue: string) => {
      // Essayer différentes APIs pour accéder à l'instance CodeMirror
      const cmInstance = codeMirrorRef.value?.cminstance ||
                        codeMirrorRef.value?.cm ||
                        codeMirrorRef.value?.codemirror ||
                        codeMirrorRef.value?.$refs?.cm ||
                        codeMirrorRef.value?.editor;

      if (cmInstance && typeof cmInstance.replaceRange === 'function') {
        // Instance CodeMirror trouvée, insertion à la position du curseur
        const cursor = cmInstance.getCursor();
        cmInstance.replaceRange(variableValue, cursor);
        cmInstance.focus();
        handleCodeChange();
      } else {
        // Fallback: insérer directement dans localCode à la fin
        localCode.value += variableValue;
        handleCodeChange();
      }
    };

    const handleCodeChange = () => {
      // Emit en temps réel pour synchronisation
      emit('update:code', localCode.value);
    };

    const handleApply = () => {
      emit('apply', localCode.value);
      emit('update:code', localCode.value);
      isVisible.value = false;
    };

    const handleCancel = () => {
      // Restaurer le code original
      localCode.value = originalCode.value;
      emit('update:code', originalCode.value);
      emit('cancel');
      isVisible.value = false;
    };

    const handleClose = () => {
      handleApply(); // Auto-apply au close
    };

    // Synchroniser le code quand la modal s'ouvre
    watch(() => props.visible, (newVisible) => {
      if (newVisible) {
        localCode.value = props.code;
        originalCode.value = props.code;

        // Charger CodeMirror APRÈS que la modale soit complètement affichée
        setTimeout(() => {
          codeMirrorReady.value = true;

          // Focus et refresh après initialisation
          nextTick(() => {
            setTimeout(() => {
              if (codeMirrorRef.value?.codemirror) {
                codeMirrorRef.value.codemirror.focus();
                codeMirrorRef.value.codemirror.refresh();
              }
            }, 100);
          });
        }, 200);
      } else {
        // Réinitialiser CodeMirror quand la modale se ferme
        codeMirrorReady.value = false;
      }
    });

    // Synchroniser le code externe
    watch(() => props.code, (newCode) => {
      if (!props.visible) {
        localCode.value = newCode;
        originalCode.value = newCode;
      }
    });

    onMounted(() => {
      // Raccourcis clavier globaux quand la modal est ouverte
      const handleKeydown = (e: KeyboardEvent) => {
        if (props.visible) {
          if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
          } else if (e.ctrlKey && e.key === 's') {
            e.preventDefault();
            handleApply();
          }
        }
      };

      document.addEventListener('keydown', handleKeydown);

      return () => {
        document.removeEventListener('keydown', handleKeydown);
      };
    });

    return {
      t,
      isVisible,
      localCode,
      codeMirrorRef,
      codeMirrorReady,
      cmOptions,
      getLanguageIcon,
      insertVariable,
      handleCodeChange,
      handleApply,
      handleCancel,
      handleClose,
    };
  },
});
